import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Link as ChakraLink,
  FormErrorMessage
} from '@chakra-ui/react';
import { LogIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { useLoginMutation } from '@state/apiSlice/requests';
import { setCredentials } from '@state/userSlice/userSlice';
import type { AppDispatch } from "@state/store";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { fetchInit } from '@state/userSlice/userSlice';

const LoginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(6, 'Too Short!').required('Password is required'),
});

export const Login = () => {
  const [login] = useLoginMutation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  
  const handleSubmit = async (values: { email: string; password: string }, actions: any) => {
    try {
      const res = await login(values).unwrap();
      dispatch(setCredentials(res));
      await dispatch(fetchInit({})).unwrap();
      navigate("/"); // ✅ Redirect on successful login
    } catch (err) {
      actions.setFieldError('password', 'Invalid credentials');
    } finally {
      actions.setSubmitting(false);
    }
  };
  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={4} bg="white">
      <VStack
        spacing={8}
        w="full"
        maxW="400px"
        bg="white"
        p={8}
        borderRadius="lg"
        boxShadow="xl"
        border="1px"
        // borderColor="gray.700"
      >
        <Heading size="lg" color="black">
          Login
        </Heading>

        <Formik
          initialValues={{ email: '', password: '' }}
          validationSchema={LoginSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form style={{ width: '100%' }}>
              <VStack spacing={4}>
                <Field name="email">
                  {({ field, form }: any) => (
                    <FormControl isInvalid={form.errors.email && form.touched.email}>
                      <FormLabel color="black">Email</FormLabel>
                      <Input
                        {...field}
                        type="email"
                        placeholder="your@email.com"
                        bg="white"
                        border="1px"
                        color="black"
                        borderColor="black"

                      />
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Field name="password">
                  {({ field, form }: any) => (
                    <FormControl isInvalid={form.errors.password && form.touched.password}>
                      <FormLabel color="black">Password</FormLabel>
                      <Input
                        {...field}
                        type="password"
                        placeholder="••••••••"
                        bg="white"
                        border="1px"
                        borderColor="black"

                        color="black"
                      />
                      <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Button
                  type="submit"
                  w="full"
                  bg="#ff3d00"
                  color="white"
                  leftIcon={<LogIn size={20} />}
                  isLoading={props.isSubmitting}
                  _hover={{
                    bg: '#e53700',
                    boxShadow: '0 0 20px rgba(255, 61, 0, 0.5)',
                  }}
                >
                  Sign In
                </Button>

                <Text color="gray.400">
                  Forgot your password?{' '}
                  <ChakraLink
                    as={Link}
                    to="/ap/forgot-password"
                    color="cyan.400"
                    _hover={{ color: 'cyan.300' }}
                  >
                    Reset it here
                  </ChakraLink>
                </Text>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    </Box>
  );
};
